/**
 * Автопилот — за игрока, который не бот, временно ходит бот.
 *
 * В промо-матче против LLM-агента агент может не подключиться к старту или
 * отвалиться посреди партии (см. utils/aiPromo/aiAgentControl). Тогда за него
 * доигрывает серверный бот, а вернувшийся агент забирает ходы обратно.
 *
 * Движок бота решает по player.isBot, за кого ходить, кого подтверждать на
 * этапе ближнего боя и кто из людей завершает этап. Поэтому, как и с
 * перехватом ботов админом (controlledBots), правила по движку не расставляются:
 * ему отдается состояние, где игроки на автопилоте помечены ботами
 * (isBot: true). Это зеркальная маска к maskControlledBots: там бот выглядит
 * живым, здесь живой — ботом. Реальное состояние не меняется — маска только
 * на чтение, и в общее состояние, рассылку и запись матча пометка не попадает:
 * для всех остальных игрок остается собой.
 *
 * Порядок с маской перехваченных ботов — сначала автопилот, потом перехват:
 *
 *   store = createAutopilotMaskedStore(store, autopilotPlayerIds);
 *   store = createControlledBotsMaskedStore(store, controlledBotIds);
 *
 * Маска перехвата снимает пометку бота с тех, кем вручную управляет онлайн-админ.
 * Стоя второй, она видит уже и пометку автопилота, поэтому если один и тот же
 * игрок окажется в обоих списках, последнее слово за живым человеком: движок
 * за него не ходит. Сейчас списки не пересекаются (перехватить можно только
 * настоящего бота, см. getControlledBotIds), порядок — страховка на будущее.
 */

// Маски по ссылке на состояние: на один и тот же снимок — одна и та же копия.
// Слабые ключи: снимок, который больше никто не держит, уходит вместе с маской
const maskedStatesCache = new WeakMap();

// Ключ набора игроков: от порядка id маска не зависит
const getPlayerIdsKey = (playerIds) => [...playerIds].sort().join('\u0000');

const buildAutopilotMaskedState = (state, autopilotIds) => {
  const gameState = state?.shared?.gameState;
  if (!gameState || !Array.isArray(gameState.players)) {
    return state;
  }
  if (!gameState.players.some((player) => !player.isBot && autopilotIds.has(player.id))) {
    return state;
  }

  return {
    ...state,
    shared: {
      ...state.shared,
      gameState: {
        ...gameState,
        players: gameState.players.map((player) => (
          autopilotIds.has(player.id) && !player.isBot ? {...player, isBot: true} : player
        )),
      },
    },
  };
};

/**
 * Состояние, в котором перечисленные игроки выглядят для движка ботами.
 * Возвращает исходный объект, если маскировать нечего (пустой список, таких
 * игроков нет или они уже боты), — чтобы не плодить копии на каждом чтении.
 * Для одного и того же состояния и набора id возвращает один и тот же объект.
 *
 * @param {Object} state - корневое состояние (state.shared.gameState.players)
 * @param {string[]} playerIds - id игроков на автопилоте
 * @returns {Object}
 */
export const maskAutopilotPlayers = (state, playerIds) => {
  if (!playerIds || playerIds.length === 0 || !state || typeof state !== 'object') {
    return state;
  }

  const key = getPlayerIdsKey(playerIds);
  let masksByKey = maskedStatesCache.get(state);
  if (masksByKey && masksByKey.has(key)) {
    return masksByKey.get(key);
  }

  const masked = buildAutopilotMaskedState(state, new Set(playerIds));
  if (!masksByKey) {
    masksByKey = new Map();
    maskedStatesCache.set(state, masksByKey);
  }
  masksByKey.set(key, masked);
  return masked;
};

/**
 * Обертка стора для движка бота: чтение — через маску автопилота, запись — в
 * настоящий стор без изменений.
 *
 * Метку авто-действий (autoActionsTrackerKey, см. executePhaseAutoActions)
 * обертка держит на оборачиваемом сторе, как и createControlledBotsMaskedStore:
 * сервер создает обертки заново на каждый прогон, и с маской и без нее прогоны
 * должны сходиться на один ключ — иначе включение автопилота посреди фазы
 * раздало бы снабжение второй раз.
 */
export const createAutopilotMaskedStore = (store, playerIds) => {
  if (!playerIds || playerIds.length === 0) {
    return store;
  }

  const ids = [...playerIds];
  let lastRawState = null;
  let lastMaskedState = null;

  return {
    ...store,
    autoActionsTrackerKey: store.autoActionsTrackerKey || store,
    getState: () => {
      const rawState = store.getState();
      if (rawState !== lastRawState) {
        lastRawState = rawState;
        lastMaskedState = maskAutopilotPlayers(rawState, ids);
      }
      return lastMaskedState;
    },
    dispatch: (action) => store.dispatch(action),
  };
};
